import React from 'react';
import { Box, Typography } from '@mui/material';
import { useAppSelector, useAppDispatch } from '@/app/hooks';
import { useNavigate } from 'react-router-dom';
import {
  selectNewsResults,
  selectEventsResults,
  selectInstitutionalResults,
  selectSitesResults,
  selectFormsResults,
} from '@/features/search/searchSelectors';
import { SearchTab } from '@/types/search.types';
import { ItemsModal } from '@/components/common/ItemsModal';
import { NewsDetailModalContent } from './NewsDetailModalContent';
import { NewsResultCard } from './NewsResultCard';
import { EventResultCard } from './EventResultCard';
import { InstitutionalDocumentCard } from './InstitutionalDocumentCard';
import { SiteResultCard } from './SiteResultCard';
import { FormResultCard } from './FormResultCard';
import { useDocumentDownload } from './hooks/useDocumentDownload';
import { useNewsModal } from './hooks/useNewsModal';
import { formatDate } from './utils/dateFormatters';

interface SearchResultsProps {
  activeTab: SearchTab;
  onClose: () => void;
}

const SearchResults: React.FC<SearchResultsProps> = ({ activeTab, onClose }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const newsResults = useAppSelector(selectNewsResults);
  const eventsResults = useAppSelector(selectEventsResults);
  const institutionalResults = useAppSelector(selectInstitutionalResults);
  const sitesResults = useAppSelector(selectSitesResults);
  const formsResults = useAppSelector(selectFormsResults);

  const { downloading, handleDownload } = useDocumentDownload(dispatch);
  const {
    isOpen: newsDetailModalOpen,
    selectedItem: selectedNewsItem,
    imageUrl: selectedImageUrl,
    openModal: handleNavigateToNews,
    closeModal: handleCloseNewsDetailModal,
    downloadDocument: handleDownloadDocument,
  } = useNewsModal(dispatch);

  const handleNavigateToSite = () => {
    navigate('/sites');
    onClose();
  };

  const renderAllResults = () => (
    <Box>
      {newsResults.length > 0 && (
        <Box mb={3}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'primary.dark' }}>
            Noticias ({newsResults.length})
          </Typography>
          {newsResults.map((article) => (
            <NewsResultCard
              key={article.id}
              article={article}
              onNavigate={handleNavigateToNews}
            />
          ))}
        </Box>
      )}

      {eventsResults.length > 0 && (
        <Box mb={3}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'primary.dark' }}>
            Eventos ({eventsResults.length})
          </Typography>
          {eventsResults.map((event) => (
            <EventResultCard key={event.id} event={event} />
          ))}
        </Box>
      )}

      {institutionalResults.length > 0 && (
        <Box mb={3}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'primary.dark' }}>
            Documentos Institucionales ({institutionalResults.length})
          </Typography>
          {institutionalResults.map((doc) => (
            <InstitutionalDocumentCard
              key={doc.id}
              document={doc}
              downloading={downloading}
              onDownload={handleDownload}
            />
          ))}
        </Box>
      )}

      {sitesResults.length > 0 && (
        <Box mb={3}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'primary.dark' }}>
            Sedes ({sitesResults.length})
          </Typography>
          {sitesResults.map((site) => (
            <SiteResultCard
              key={site.id}
              site={site}
              onNavigate={handleNavigateToSite}
            />
          ))}
        </Box>
      )}

      {formsResults.length > 0 && (
        <Box mb={3}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'primary.dark' }}>
            Formularios ({formsResults.length})
          </Typography>
          {formsResults.map((form) => (
            <FormResultCard
              key={form.id}
              form={form}
              downloading={downloading}
              onDownload={handleDownload}
            />
          ))}
        </Box>
      )}
    </Box>
  );

  return (
    <>
      <Box sx={{ maxHeight: '50vh', overflowY: 'auto', pr: 1 }}>
        {activeTab === SearchTab.ALL && renderAllResults()}

        {activeTab === SearchTab.NEWS && (
          newsResults.length > 0 ? (
            newsResults.map((article) => (
              <NewsResultCard
                key={article.id}
                article={article}
                onNavigate={handleNavigateToNews}
              />
            ))
          ) : (
            <Typography color="text.secondary" textAlign="center" py={4}>
              No se encontraron noticias
            </Typography>
          )
        )}

        {activeTab === SearchTab.EVENTS && (
          eventsResults.length > 0 ? (
            eventsResults.map((event) => (
              <EventResultCard key={event.id} event={event} />
            ))
          ) : (
            <Typography color="text.secondary" textAlign="center" py={4}>
              No se encontraron eventos
            </Typography>
          )
        )}

        {activeTab === SearchTab.INSTITUTIONAL && (
          institutionalResults.length > 0 ? (
            institutionalResults.map((doc) => (
              <InstitutionalDocumentCard
                key={doc.id}
                document={doc}
                downloading={downloading}
                onDownload={handleDownload}
              />
            ))
          ) : (
            <Typography color="text.secondary" textAlign="center" py={4}>
              No se encontraron documentos institucionales
            </Typography>
          )
        )}

        {activeTab === SearchTab.SITES && (
          sitesResults.length > 0 ? (
            sitesResults.map((site) => (
              <SiteResultCard
                key={site.id}
                site={site}
                onNavigate={handleNavigateToSite}
              />
            ))
          ) : (
            <Typography color="text.secondary" textAlign="center" py={4}>
              No se encontraron sedes
            </Typography>
          )
        )}

        {activeTab === SearchTab.FORMS && (
          formsResults.length > 0 ? (
            formsResults.map((form) => (
              <FormResultCard
                key={form.id}
                form={form}
                downloading={downloading}
                onDownload={handleDownload}
              />
            ))
          ) : (
            <Typography color="text.secondary" textAlign="center" py={4}>
              No se encontraron formularios
            </Typography>
          )
        )}
      </Box>

      {/* Modal de Detalle de Noticia */}
      <ItemsModal
        open={newsDetailModalOpen}
        onClose={handleCloseNewsDetailModal}
        title={selectedNewsItem?.title || 'Detalle de Noticia'}
        showDownloadButton={selectedNewsItem?.isNews === false && !!selectedNewsItem?.docName}
        onDownload={handleDownloadDocument}
      >
        {selectedNewsItem && (
          <NewsDetailModalContent
            newsItem={selectedNewsItem}
            imageUrl={selectedImageUrl}
            formatDate={formatDate}
          />
        )}
      </ItemsModal>
    </>
  );
};

export default SearchResults;
